import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, combineLatest, of, timer } from 'rxjs';
import {
  map,
  exhaustMap,
  catchError,
  withLatestFrom,
  switchMap,
  mergeMap,
  tap,
  auditTime,
  filter,
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import {
  LOAD_SCHEDULER_COLUMNS,
  INIT_ASSIGNED_COLUMNS,
  INIT_AVAILABLE_COLUMNS,
  TOGGLE_AVAILABLE_COLUMN,
  REORDER_AVAILABLE_COLUMN,
  RESET_AVAILABLE_COLUMNS,
  CHANGE_WIDTH_AVAILABLE_COLUMN,
  TOGGLE_SCHEDULED_COLUMN,
  REORDER_SCHEDULED_COLUMN,
  RESET_SCHEDULED_COLUMNS,
  CHANGE_WIDTH_SCHEDULED_COLUMN,
  scheduleJobAction,
  unscheduleJobAction,
} from './actions';

import {
  selectAvailableJobColumns,
  selectAvailableJobs,
  selectSchedule,
  selectScheduledJobColumns,
  selectScheduleOverlay,
} from './selectors';
import {
  PendingScheduleOp,
  buildScheduleOp,
  buildUnscheduleOp,
  findExpiredOps,
  findLandedJobs,
  newScheduleOpId,
  scheduleOpAccepted,
  scheduleOpFailed,
  scheduleOpStarted,
  scheduleOpUnconfirmed,
  scheduleOverlayPruned,
} from './schedule-overlay';
import { putAction } from '../../clientData.actions';
import { apiErrorMessages } from '../../../utils/api-errors';

import { ApiService } from '../../api.service';

declare let gtag;

/**
 * Statuses after which a schedule change may or may not have been applied: we stopped waiting (504,
 * a dropped connection), or something failed after the Agent had already written (500). Everything
 * else - 400 rejected, 401/403 not allowed, 503 Agent not running or busy - means it was not.
 */
export const isUnknownOutcome = (status: number) =>
  status === 0 || (status >= 500 && status !== 503);

@Injectable()
export class SchedulerEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private apiService: ApiService,
    private _snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  loadAvaColumns$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_SCHEDULER_COLUMNS),
      exhaustMap(() =>
        this.apiService.fetchColumns('AvailableJobColumns', { skip: 0, take: 1000 }).pipe(
          map((columns) => ({ type: INIT_AVAILABLE_COLUMNS, columns })),
          catchError(() => EMPTY)
        )
      )
    );
  });

  loadSchColumns$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_SCHEDULER_COLUMNS),
      exhaustMap(() =>
        this.apiService.fetchColumns('ScheduledJobColumns', { skip: 0, take: 1000 }).pipe(
          map((columns) => ({ type: INIT_ASSIGNED_COLUMNS, columns })),
          catchError(() => EMPTY)
        )
      )
    );
  });

  postAvailableColumnSelectionToTheServer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TOGGLE_AVAILABLE_COLUMN,
          REORDER_AVAILABLE_COLUMN,
          RESET_AVAILABLE_COLUMNS,
          CHANGE_WIDTH_AVAILABLE_COLUMN
        ),
        withLatestFrom(this.store.pipe(select(selectAvailableJobColumns))),
        switchMap(([, columns]) =>
          this.apiService.updateColumns('AvailableJobColumns', columns).pipe(
            map((columns) => EMPTY),
            catchError(() => EMPTY)
          )
        )
      ),
    { dispatch: false }
  );

  postScheduledColumnSelectionToTheServer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TOGGLE_SCHEDULED_COLUMN,
          REORDER_SCHEDULED_COLUMN,
          RESET_SCHEDULED_COLUMNS,
          CHANGE_WIDTH_SCHEDULED_COLUMN
        ),
        withLatestFrom(this.store.pipe(select(selectScheduledJobColumns))),
        switchMap(([, columns]) =>
          this.apiService.updateColumns('ScheduledJobColumns', columns).pipe(
            map((columns) => EMPTY),
            catchError(() => EMPTY)
          )
        )
      ),
    { dispatch: false }
  );

  /**
   * Shows the move at once as a pending op (see schedule-overlay.ts), then posts it. The grid
   * follows the server's Schedule/AvailableJob collections, which only change when the sync lands
   * - minutes, for thousands of jobs - so without the op nothing on screen would move until then.
   */
  scheduleJob$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(scheduleJobAction),
        withLatestFrom(this.store.select(selectSchedule), this.store.select(selectAvailableJobs)),
        mergeMap(([action, schedules, availableJobs]) => {
          gtag('event', 'scheduler_scheduleJob', {
            event_catetory: 'scheduler',
            event_label: action.payload.isOnMachine ? 'toMachine' : 'toSchedule',
            value: action.payload.jobIds.length,
          });
          const op = buildScheduleOp(
            newScheduleOpId(),
            action.payload,
            schedules,
            availableJobs,
            Date.now()
          );
          if (op) {
            this.store.dispatch(scheduleOpStarted({ op }));
          }
          return this.apiService.updateScheduleJobs(action.payload).pipe(
            tap((estimates) => {
              estimates.forEach((s) =>
                this.store.dispatch(putAction({ collection: 'MachineSchedule', payload: s }))
              );
              if (op) {
                this.store.dispatch(scheduleOpAccepted({ opId: op.opId, at: Date.now() }));
              }
            }),
            catchError((ex) => {
              this.onScheduleChangeError(ex, op);
              return of(); // Prevent the effect from stopping
            })
          );
        })
      ),
    { dispatch: false }
  );

  unScheduleJob$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(unscheduleJobAction),
        withLatestFrom(this.store.select(selectSchedule), this.store.select(selectAvailableJobs)),
        mergeMap(([action, schedules, availableJobs]) => {
          // Google Analytics tracking
          gtag('event', 'scheduler_unScheduleJob', {
            event_category: 'scheduler',
            value: action.payload.scheduledJobIds.length,
          });
          const op = buildUnscheduleOp(
            newScheduleOpId(),
            action.payload,
            schedules,
            availableJobs,
            Date.now()
          );
          if (op) {
            this.store.dispatch(scheduleOpStarted({ op }));
          }

          return this.apiService.removeScheduleJobs(action.payload).pipe(
            tap((estimates) => {
              estimates.forEach((s) =>
                this.store.dispatch(putAction({ collection: 'MachineSchedule', payload: s }))
              );
              if (op) {
                this.store.dispatch(scheduleOpAccepted({ opId: op.opId, at: Date.now() }));
              }
            }),
            catchError((error) => {
              this.onScheduleChangeError(error, op);
              return of(); // Ensure observable completes
            })
          );
        })
      ),
    { dispatch: false } // Ensures no implicit action dispatch
  );

  /**
   * Retires pending schedule moves: each job once both server lists show it in its new place, and
   * whole ops that stop making progress (the server's lists then show as they are). Only runs while
   * there are pending ops.
   */
  pruneScheduleOverlay$ = createEffect(() =>
    this.store.select(selectScheduleOverlay).pipe(
      switchMap((overlay) =>
        overlay.ops.length === 0
          ? EMPTY
          : combineLatest([
              this.store.select(selectSchedule),
              this.store.select(selectAvailableJobs),
              // Re-checks expiry even when no data arrives.
              timer(0, 15000),
            ]).pipe(
              auditTime(500),
              map(([schedules, availableJobs]) => ({
                landed: findLandedJobs(overlay.ops, schedules, availableJobs),
                expired: findExpiredOps(overlay.ops, Date.now()),
              }))
            )
      ),
      filter(({ landed, expired }) => Object.keys(landed).length > 0 || expired.length > 0),
      tap(({ expired }) => {
        const count = expired
          .filter((op) => op.state !== 'sending')
          .reduce((sum, op) => sum + op.ordIds.length, 0);
        if (count > 0) {
          this.toast(this.translate.instant('scheduleJobsNotSyncedBack', { count }), true);
        }
      }),
      map(({ landed, expired }) =>
        scheduleOverlayPruned({ landed, expired: expired.map((op) => op.opId), at: Date.now() })
      )
    )
  );

  /**
   * A definite failure takes the pending move back off the screen. An unknown outcome - we stopped
   * waiting, but the Agent may still be writing - leaves it up as unconfirmed for the sync to settle.
   */
  private onScheduleChangeError(ex: HttpErrorResponse, op: PendingScheduleOp | null) {
    const status = ex?.status ?? 0;
    if (isUnknownOutcome(status)) {
      if (op) {
        this.store.dispatch(scheduleOpUnconfirmed({ opId: op.opId, at: Date.now() }));
      }
      console.warn(`Schedule change outcome unknown (${status}).`, apiErrorMessages(ex));
      this.toast(this.translate.instant('scheduleChangeTakingLong'));
      return;
    }

    if (op) {
      this.store.dispatch(scheduleOpFailed({ opId: op.opId }));
    }
    const reason =
      status === 400
        ? apiErrorMessages(ex).join(' ')
        : status === 401 || status === 403
          ? this.translate.instant('scheduleNotPermitted')
          : this.translate.instant('scheduleAgentUnavailable');
    const msg = `${this.translate.instant('scheduleChangeNotSaved')} ${reason}`;
    console.error(`Error: Schedule change was not saved. [${status}] ${ex?.statusText}`, reason);
    this.toast(msg, true);
  }

  /** `sticky` messages stay until dismissed: they report something the user has to act on. */
  private toast(textContent: string, sticky = false) {
    this._snackBar.open(textContent, sticky ? this.translate.instant('dismiss') : '', {
      horizontalPosition: 'right',
      verticalPosition: 'top',
      duration: sticky ? undefined : 6000,
    });
  }
}
